import React, {useEffect, useState} from 'react';
import AurUserDetailsView from '../user-management/aur-user-details-view/AurUserDetailsView';
import AurProfilePicture from '../common/aur-profile-picture/AurProfilePicture';
import AurEditDetails from '../common/aur-edit-details/AurEditDetails';
import {labelProps} from '../user-management/aur-user-details-view/models';
import {EDIT_TEAM_MEMBER, ADD_NEW_TEAM_MEMBER} from '../../constants/constants';
import {
  ITeamMemberInfoFormData,
  TeamMemberStatus,
} from '../pages/user-management/models';
import {updateTeamMember} from '../../api/UserManagementService';
import {useDispatch} from 'react-redux';
import {setTeamMember} from '../../redux/slices/teamMemberSlice';
import {setShowModal} from '../../redux/slices/unSaveChangesModalSlice';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../constants/analyticsConstants';
import {useAnalytics} from '../../hooks/useAnalytics';
import {t} from 'i18next';
import AurLoadingIndicator from '../common/aur-loading-indicator/AurLoadingIndictor';

interface Props {
  userId: string;
  userFirstName: string;
  userLastName: string;
  userEmail: string;
  userListDisabled: boolean;
  setUserListDisabled: (userListDisabled: boolean) => void;
  setUserFirstName?: (userFirstName: string) => void;
  setUserLastName?: (userLastName: string) => void;
  setActiveStep?: ((value: number) => void | undefined) | undefined;
  flowName: string;
  labelDetails?: labelProps[];
  activeStep?: number;
  createdByName?: string;
  updatedByName?: string;
  createdByDate?: string;
  updatedByDate?: string;
  onSysAdminRoleChanged?: (change: 'promoted' | 'demoted') => void;
  onEditChange?: (isEdit: boolean) => void;
}

const UserDetailsView: React.FC<Props> = (props: Props) => {
  const {
    userId,
    userFirstName,
    userLastName,
    userEmail,
    setUserListDisabled,
    setUserFirstName,
    setUserLastName,
    setActiveStep,
    flowName,
    labelDetails,
    activeStep,
    createdByName = '',
    updatedByName = '',
    createdByDate = '',
    updatedByDate = '',
    onSysAdminRoleChanged,
    onEditChange,
  } = props;

  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isUpdatingMemberLoading, setIsUpdatingMemberLoading] = useState(false);
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();

  useEffect(() => {
    onEditChange?.(isEdit);
  }, [onEditChange, isEdit]);

  const onEditDetailsClick = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.users.editDetails'),
        region: GA_REGIONS.TAB,
        type: GA_CLICK_TYPES.BUTTON,
        action: t('gaTracking.users.editUserDetails'),
      },
      ...(flowName === ADD_NEW_TEAM_MEMBER && {
        subPage: {
          subpage_name: t('gaTracking.users.review'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      }),
    });
    switch (flowName) {
      case ADD_NEW_TEAM_MEMBER:
        setActiveStep?.(0);
        break;
      case EDIT_TEAM_MEMBER:
        setIsEdit(true);
        setUserListDisabled(true);
        break;
      default:
        break;
    }
  };
  const onEditCancelClick = () => {
    setIsEdit(false);
    setUserListDisabled(false);
    localStorage?.setItem('currentPath', '');
    history.back();
    dispatch(setShowModal(false));
  };
  const onUpdateUserDetails = (teamMemberInfo: ITeamMemberInfoFormData) => {
    setIsUpdatingMemberLoading(true);
    const updatedStatus = () => {
      if (
        teamMemberInfo?.status === TeamMemberStatus.invited &&
        !teamMemberInfo.isActive
      ) {
        return TeamMemberStatus.invited;
      } else if (
        teamMemberInfo?.status === TeamMemberStatus.invited &&
        teamMemberInfo.isActive
      ) {
        return TeamMemberStatus.active;
      } else if (teamMemberInfo.isActive) {
        return TeamMemberStatus.active;
      } else {
        return TeamMemberStatus.inactive;
      }
    };
    updateTeamMember({
      userId: teamMemberInfo.userId,
      lastName: teamMemberInfo.lastName,
      firstName: teamMemberInfo.firstName,
      status: updatedStatus(),
      isBillable: teamMemberInfo.isBillable,
      ipAddressList: teamMemberInfo.isLimitIp
        ? teamMemberInfo.ipAddressList
        : [],
    })
      .then((_) => {
        setIsEdit(false);
        setUserListDisabled(false);
        if (setUserFirstName) setUserFirstName(teamMemberInfo.firstName);
        if (setUserLastName) setUserLastName(teamMemberInfo.lastName);
        dispatch(setTeamMember(teamMemberInfo));
      })
      .catch((_) => {
        alert('Something went wrong');
      })
      .finally(() => {
        setIsUpdatingMemberLoading(false);
      });
  };
  return (
    <>
      {isEdit ? (
        <div className="user-details-main-container">
          <div className="user-profile-picture-container">
            <AurProfilePicture
              bgColor={true}
              displayText={
                userFirstName.charAt(0).toUpperCase() +
                userLastName.charAt(0).toUpperCase()
              }
              borderRadius={2.84}
              iconSize={91}
              textSize="Medium"
              iconType="User"
            />
          </div>
          <AurEditDetails
            onCancelClick={onEditCancelClick}
            userFirstName={userFirstName}
            userLastName={userLastName}
            isAdmin={true} //passing this value static for now, need to update once api comes
            onUpdateUserDetails={onUpdateUserDetails}
            onSysAdminRoleChanged={onSysAdminRoleChanged}
            isSpacesNotAllowed={true}
          />
        </div>
      ) : (
        <AurUserDetailsView
          userId={userId}
          userFirstName={userFirstName}
          userLastName={userLastName}
          userEmail={userEmail}
          onClick={onEditDetailsClick}
          creatorDetails={
            flowName === ADD_NEW_TEAM_MEMBER && activeStep !== 3 ? false : true
          }
          profileIconBgColor="#C4AAEB"
          createdByName={createdByName}
          updatedByName={updatedByName}
          createdByDate={createdByDate}
          updatedByDate={updatedByDate}
          labelDetails={labelDetails}
          flowName={flowName}
        />
      )}
      {flowName === EDIT_TEAM_MEMBER && isUpdatingMemberLoading && (
        <AurLoadingIndicator overlay={true} isOpen={isUpdatingMemberLoading} />
      )}
    </>
  );
};

export default UserDetailsView;
